/**
 * API utility functions and axios configuration
 */
import axios from 'axios';

/**
 * Determina URL do backend dinamicamente.
 * Usa window.location.origin para garantir que funciona em QUALQUER ambiente:
 * - Preview (preview.emergentagent.com)
 * - Deploy (deploy.emergentagent.com) 
 * - Produção (pedeefacil.com.br)
 */
export const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    if (currentHost === 'pedeefacil.com.br' || currentHost === 'www.pedeefacil.com.br') {
      return 'https://pedeefacil.com.br';
    }
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

export const BACKEND_URL = getBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors and auto-retry on 503
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Auto-retry on 503 (Service Unavailable / Circuit Breaker)
    if (error.response?.status === 503 && config && !config._retryCount) {
      config._retryCount = config._retryCount || 0;
      const maxRetries = 2;
      
      if (config._retryCount < maxRetries) {
        config._retryCount += 1;
        const retryAfter = parseInt(error.response.headers?.['retry-after'] || '3', 10);
        const delay = Math.min(retryAfter * 1000, 5000);
        
        console.warn(`[API] 503 - Retry ${config._retryCount}/${maxRetries} em ${delay/1000}s para ${config.url}`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return api(config);
      }
    }
    
    // NUNCA fazer logout por erros de rede/servidor
    const status = error.response?.status;
    const isNetworkError = !error.response || status === 503 || status === 502 || status === 504;
    
    if (status === 401 && !isNetworkError) {
      const isLoginRequest = config?.url?.includes('/login');
      if (!isLoginRequest) {
        // Não remover token aqui - deixar o interceptor principal do App.js tratar
        console.warn('[API] 401 recebido - delegando ao interceptor principal');
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions for common API operations
export const apiHelpers = {
  // Products
  getProducts: () => api.get('/products'),
  createProduct: (data) => api.post('/products', data),
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/products/${id}`),
  uploadProductImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Categories
  getCategories: () => api.get('/categories'),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),

  // Orders
  getOrders: (params) => api.get('/orders', { params }),
  updateOrderStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  
  // Dashboard
  getDashboardStats: () => api.get('/dashboard'),

  // Restaurant
  getRestaurantSettings: () => api.get('/restaurant/settings'),
  updateRestaurantSettings: (data) => api.put('/restaurant/settings', data),
  
  // Export with auth
  exportData: async (endpoint, format = 'csv') => {
    const token = localStorage.getItem('token');
    const response = await api.get(`${endpoint}?format=${format}`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` }
    });
    return response;
  }
};

export default api;
